import { useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import MetaTitle from '@/Components/UI/MetaTitle'
import PseudoHeader from '@/Components/PseudoHeader/PseudoHeader'
import LocationSection, {
  type LocationSelectionData,
} from '@/Components/PreferredLocation/LocationSection'
import ActionBar from '@/Components/UI/ActionBar'
import { type PreviousEventRequest } from '@/Components/RepeatEvent/PreviousEventCard'
import { computeCentroid } from '@/Components/PreferredLocation/LocationMapWithDrawing'
import useBriefContext from '@/Hooks/Brief/useBriefContext'
import { getCurrencyByCountry } from '@/Utilities/Location'

export interface PreferredLocationRouteState {
  eventName: string
  guests: number | ''
  dateRange: string
  time: string
  budget: number | ''
  additionalNotes: string | undefined
  requestedVenues: PreviousEventRequest[]
  originalEventName: string | undefined
  initialLocationData: LocationSelectionData | null
  event?: Record<string, unknown>
}

const PreferredLocationPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { dispatchBrief } = useBriefContext()
  const state = (location.state ?? {}) as PreferredLocationRouteState

  const venuePins = useMemo(
    () =>
      (state.requestedVenues ?? [])
        .filter((v) => v.lat != null && v.lng != null)
        .map((v) => ({ lat: v.lat!, lng: v.lng!, name: v.spaceName })),
    [state.requestedVenues]
  )

  const derivedInitialData = useMemo<LocationSelectionData | null>(() => {
    if (venuePins.length) {
      const { lat, lng } = computeCentroid(venuePins)
      return { location: '', lat, lon: lng, country: '', mode: 'radius', radiusKm: 3 }
    }
    return state.initialLocationData ?? null
  }, [state.initialLocationData, venuePins])

  const [locationData, setLocationData] = useState<LocationSelectionData | null>(derivedInitialData)

  const onContinue = () => {
    if (locationData?.country) {
      dispatchBrief({
        updateAttributes: { currency: getCurrencyByCountry(locationData.country) },
      })
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
    navigate('/summary', {
      state: { ...state, locationData },
    })
  }

  return (
    <>
      <MetaTitle title='Preferred location - HeadBox' />
      <PseudoHeader />
      <div className='mt-16 pt-6 md:pt-10 pb-36 px-4 sm:px-6'>
        <div className='w-full max-w-[1120px] mx-auto py-4 sm:py-0'>
          <LocationSection
            onChange={setLocationData}
            initialData={derivedInitialData ?? undefined}
            venuePins={venuePins.length ? venuePins : undefined}
          />
        </div>
      </div>
      <ActionBar
        primaryLabel='Save & continue'
        onPrimary={onContinue}
        primaryDisabled={locationData === null}
        secondaryLabel='Back'
        onSecondary={() =>
          navigate('/duplicate-event', { replace: true, state: { event: state.event } })
        }
        primaryWide
      />
    </>
  )
}

export default PreferredLocationPage
